import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LanguageService } from '../../../services/i18n/language';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.scss'
})
export class PaginationComponent implements OnInit, OnDestroy {
  @Input() number: number = 0; // текущая страница (0-based)
  @Input() totalPages: number = 0;
  @Input() size: number = 20;
  @Input() totalElements: number = 0;
  @Input() hasNext: boolean = false;
  @Input() hasPrevious: boolean = false;

  @Output() pageChange = new EventEmitter<number>();

  firstPageText = 'Первая страница';
  previousPageText = 'Предыдущая страница';
  nextPageText = 'Следующая страница';
  lastPageText = 'Последняя страница';
  pageInfoText = 'Страница {page} из {total} (всего: {count})';

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.firstPageText = this.t('pagination.firstPage', 'Первая страница');
    this.previousPageText = this.t('pagination.previousPage', 'Предыдущая страница');
    this.nextPageText = this.t('pagination.nextPage', 'Следующая страница');
    this.lastPageText = this.t('pagination.lastPage', 'Последняя страница');
    this.pageInfoText = this.t('pagination.pageInfo', 'Страница {page} из {total} (всего: {count})');
  }

  get pageInfoResolved(): string {
    return this.pageInfoText
      .replace('{page}', String(this.number + 1))
      .replace('{total}', String(this.totalPages))
      .replace('{count}', String(this.totalElements));
  }

  get currentPage(): number {
    return this.number;
  }

  get pageSize(): number {
    return this.size;
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.totalPages && page !== this.number) {
      this.pageChange.emit(page);
    }
  }

  goToFirst(): void {
    this.goToPage(0);
  }

  goToLast(): void {
    this.goToPage(this.totalPages - 1);
  }

  goToNext(): void {
    if (this.hasNext) {
      this.goToPage(this.number + 1);
    }
  }

  goToPrevious(): void {
    if (this.hasPrevious) {
      this.goToPage(this.number - 1);
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPages = 5;
    let startPage = Math.max(0, this.number - Math.floor(maxPages / 2));
    let endPage = Math.min(this.totalPages - 1, startPage + maxPages - 1);

    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(0, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }
}
